import { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Mail, CheckCircle2, AlertCircle, Eye, Paperclip } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../../../../foundation/altzero_base/base/utils/supabaseClient';
import { emailApiService, EmailAccount, EmailTemplate } from '../../services/email/emailApiService';
import { getTenderWithSites } from '../../services/tenderService';
import { getOffers } from '../../services/tenderApiService';
import { salesforceImportService } from '../../services/salesforceImportService';
import { replacePlaceholders } from '../../utils/templateUtils';
import { buildOfferComparisonEmailHtml } from '../../utils/offerEmailTable';
import { useDocumentForm } from '../../contexts/DocumentFormContext';
import type { Offer } from '../../types/offerTypes';

type DialogState = 'loading' | 'ready' | 'sending' | 'sent' | 'error';

type SendProposalToClientDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  tenderId: string;
  tenderName: string;
  filePath: string | null;
  clientId: string;
  clientName: string;
  onSent: () => void;
};

// Signature boundary marker — same as EmailCompositionDialog
const SIG_MARKER = '<!--email-sig-->';

export const SendProposalToClientDialog = ({
  isOpen,
  onClose,
  tenderId,
  tenderName,
  filePath,
  clientId,
  clientName,
  onSent,
}: SendProposalToClientDialogProps) => {
  const { sfTenderId } = useDocumentForm();
  const [dialogState, setDialogState] = useState<DialogState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [iframeReloadKey, setIframeReloadKey] = useState(0);
  const editableIframeRef = useRef<HTMLIFrameElement>(null);

  const [userSignature, setUserSignature] = useState('');

  const [attachments, setAttachments] = useState<File[]>([]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (accepted: File[]) => setAttachments(prev => [...prev, ...accepted]),
    accept: { 'application/pdf': ['.pdf'] },
  });

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const [offers, setOffers] = useState<Offer[]>([]);
  const [includeOffersTable, setIncludeOffersTable] = useState(true);
  const [markSentToClient, setMarkSentToClient] = useState(true);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDialogState('loading');
      setErrorMessage(null);
      setAccounts([]);
      setSelectedAccount('');
      setTo('');
      setCc('');
      setSubject('');
      setBody('');
      setIframeReloadKey(0);
      setUserSignature('');
      setAttachments([]);
      setTemplates([]);
      setSelectedTemplateId('');
      setOffers([]);
      setIncludeOffersTable(true);
      setMarkSentToClient(true);
      return;
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Signature helpers ────────────────────────────────────────────────────────
  const appendSignatureToBody = (html: string, sig: string): string => {
    if (!sig?.trim()) return html;
    return (
      html +
      `\n${SIG_MARKER}<div style="margin-top:16px;font-size:0.78em;line-height:1.4;color:#555;"><hr style="border:none;border-top:1px solid #e2e8f0;margin-bottom:10px;"/>${sig}</div>`
    );
  };

  const stripSignatureFromBody = (html: string): string => {
    const idx = html.indexOf(SIG_MARKER);
    return idx !== -1 ? html.slice(0, idx) : html;
  };

  // Template authors control exactly where the offers table lands by placing this
  // token anywhere in the email body — mid-message, above/below the signature, etc.
  const OFFERS_TABLE_PLACEHOLDER = '{{Offers Table}}';

  // If the template has the placeholder, it wins (and gets cleared even when the
  // table is excluded, so the literal token never leaks into a sent email).
  // Otherwise fall back to inserting just before the signature, then before
  // </body>, then at the very end — for templates that don't use the token yet.
  const insertOffersTable = (html: string, tableHtml: string): string => {
    if (html.includes(OFFERS_TABLE_PLACEHOLDER)) {
      return html.split(OFFERS_TABLE_PLACEHOLDER).join(tableHtml);
    }
    if (!tableHtml) return html;
    const sigIdx = html.indexOf(SIG_MARKER);
    if (sigIdx !== -1) return html.slice(0, sigIdx) + tableHtml + html.slice(sigIdx);
    const bodyCloseIdx = html.lastIndexOf('</body>');
    if (bodyCloseIdx !== -1) return html.slice(0, bodyCloseIdx) + tableHtml + html.slice(bodyCloseIdx);
    return html + tableHtml;
  };

  // Wraps the injected table so a later toggle can find and remove exactly that
  // block without disturbing anything the user typed around it.
  const TABLE_START = '<!--offers-table-start-->';
  const TABLE_END = '<!--offers-table-end-->';

  const stripOffersTableMarkers = (html: string): string => {
    const startIdx = html.indexOf(TABLE_START);
    const endIdx = html.indexOf(TABLE_END);
    if (startIdx === -1 || endIdx === -1) return html;
    return html.slice(0, startIdx) + html.slice(endIdx + TABLE_END.length);
  };

  // Single source of truth for "what the body should look like right now" —
  // used for the initial template render, template/checkbox changes, and at
  // send time, so the live preview is always exactly what gets sent.
  const composeBodyWithTable = (html: string, include: boolean, offersList: Offer[]): string => {
    const stripped = stripOffersTableMarkers(html);
    const tableHtml = include && offersList.length > 0
      ? TABLE_START + buildOfferComparisonEmailHtml(offersList) + TABLE_END
      : '';
    return insertOffersTable(stripped, tableHtml);
  };

  // Reads the live edited content from the iframe
  const getBodyHtml = (): string => {
    const doc = editableIframeRef.current?.contentDocument;
    if (doc) return doc.documentElement.outerHTML;
    return body;
  };

  // ── Placeholder data builder ─────────────────────────────────────────────────
  const buildPlaceholderData = (tender: any, sites: any[]) => {
    const wfSites: any[] = tender?.workflow_data?.step1?.sites || tender?.workflow_data?.sites || [];
    const wf = wfSites[0];
    const s0 = sites[0];

    const pick = (...vals: (string | null | undefined)[]) =>
      vals.find(v => v != null && v !== '') || '';

    const siteAddr = wf?.address
      ? [wf.address.street, wf.address.city, wf.address.state, wf.address.postcode].filter(Boolean).join(', ')
      : [s0?.addr_street, s0?.addr_city, s0?.addr_state, s0?.addr_postcode].filter(Boolean).join(', ');

    return {
      retailerName: '',
      tenderName: tender?.opportunity_name || tenderName || 'Proposal',
      contactName: clientName || 'Client',
      organizationName: 'Choice Energy',
      entityName: pick(tender?.entity_name, tender?.workflow_data?.step1?.clientData?.name, clientName),
      abn: pick(wf?.abn, tender?.abn, s0?.abn),
      acn: pick(wf?.acn, tender?.acn, s0?.acn),
      startDate: pick(wf?.currentContractExpiry, tender?.current_contract_expiry, s0?.current_contract_expiry),
      pricingDueDate: '',
      comms: pick(wf?.retailerComms, tender?.retailer_comms),
      dma: pick(wf?.dmaProvider, tender?.dma_provider),
      address: siteAddr,
      contractTerms: '12, 24, 36 & 48 Month',
      specialRequests: '',
      solarCapacity: '',
      estimatedGeneration: '',
      estimatedFeedIn: '',
      sites: wfSites.length > 0
        ? wfSites.map((site: any) => ({
            id: site.id || '',
            name: site.name || '',
            nmi: site.nmi || '',
            address: {
              street: site.address?.street || '',
              city: site.address?.city || '',
              state: site.address?.state || '',
              postcode: site.address?.postcode || '',
            },
            annualKwhPeak: site.annualKwhPeak ?? null,
            annualKwhOffpeak: site.annualKwhOffpeak ?? null,
            annualKwhShoulder: site.annualKwhShoulder ?? null,
          }))
        : sites.map(site => ({
            id: site.id,
            name: site.name || '',
            nmi: site.nmi || '',
            address: {
              street: site.addr_street || '',
              city: site.addr_city || '',
              state: site.addr_state || '',
              postcode: site.addr_postcode || '',
            },
            annualKwhPeak: site.annual_kwh_peak ?? null,
            annualKwhOffpeak: site.annual_kwh_offpeak ?? null,
            annualKwhShoulder: site.annual_kwh_shoulder ?? null,
          })),
    };
  };

  const applyTemplate = (template: EmailTemplate, tender: any, sites: any[], sig: string, offersList: Offer[] = offers) => {
    const data = buildPlaceholderData(tender, sites);
    setSubject(replacePlaceholders(template.subject, data));
    const bodyHtml = replacePlaceholders(template.body_html, data);
    const withSig = appendSignatureToBody(bodyHtml, sig);
    setBody(composeBodyWithTable(withSig, includeOffersTable, offersList));
    setIframeReloadKey(k => k + 1);
  };

  // ── Initial load ─────────────────────────────────────────────────────────────
  const loadInitialData = async () => {
    setDialogState('loading');
    setErrorMessage(null);

    try {
      const [accountsData, clientResult, templatesData, tenderResult, offersResult] = await Promise.all([
        emailApiService.getAccounts(),
        supabase.from('client').select('extn, name').eq('id', clientId).single(),
        emailApiService.getTemplates('client_proposal'),
        getTenderWithSites(tenderId),
        getOffers(tenderId),
      ]);

      if (!mountedRef.current) return;

      const freshOffers: Offer[] = offersResult.success ? (offersResult.offers ?? []) : [];
      setAccounts(accountsData);
      setOffers(freshOffers);

      const defaultAcc =
        accountsData.find((a) => a.is_default && a.is_active) ??
        accountsData.find((a) => a.is_active);
      if (defaultAcc) setSelectedAccount(defaultAcc.id);

      const sig = defaultAcc?.email_signature || '';
      setUserSignature(sig);

      const extn = clientResult.data?.extn as Record<string, unknown> | null;
      const clientEmail = typeof extn?.email === 'string' ? extn.email : '';
      setTo(clientEmail);

      setTemplates(templatesData);

      const freshTender = tenderResult.success ? (tenderResult.tender ?? null) : null;
      const freshSites = tenderResult.success ? (tenderResult.sites ?? []) : [];

      const defaultTemplate = templatesData.find(t => t.is_default) ?? templatesData[0] ?? null;
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
        applyTemplate(defaultTemplate, freshTender, freshSites, sig, freshOffers);
      } else {
        const safeName = tenderName || 'Proposal';
        setSubject(`Your Energy Proposal — ${safeName}`);
        const fallbackBody = `<p>Dear ${clientName || 'Client'},</p>
<p>Please find attached your energy proposal for <strong>${safeName}</strong>.</p>
<p>We have analysed the available market offers and prepared a comprehensive proposal outlining our recommended solution.</p>
<p>Please review the attached proposal at your convenience. If you have any questions or would like to discuss the details, please don't hesitate to contact us.</p>
<p>Kind regards,</p>`;
        const withSig = appendSignatureToBody(fallbackBody, sig);
        setBody(composeBodyWithTable(withSig, includeOffersTable, freshOffers));
        setIframeReloadKey(k => k + 1);
      }

      setDialogState('ready');
    } catch (err: unknown) {
      if (!mountedRef.current) return;
      const msg = err instanceof Error ? err.message : 'Failed to load data';
      setErrorMessage(msg);
      setDialogState('error');
    }
  };

  // ── Template change ──────────────────────────────────────────────────────────
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    getTenderWithSites(tenderId).then(result => {
      const tender = result.success ? result.tender ?? null : null;
      const sites = result.success ? result.sites ?? [] : [];
      applyTemplate(template, tender, sites, userSignature);
    });
  };

  // ── Account change — swap signature ─────────────────────────────────────────
  const handleAccountChange = (accountId: string) => {
    setSelectedAccount(accountId);
    const newAcc = accounts.find(a => a.id === accountId);
    const newSig = newAcc?.email_signature || '';
    setUserSignature(newSig);
    setBody(prev => appendSignatureToBody(stripSignatureFromBody(prev), newSig));
    setIframeReloadKey(k => k + 1);
  };

  // ── Offers table toggle — recompose the live preview immediately ───────────
  const handleToggleOffersTable = (checked: boolean) => {
    setIncludeOffersTable(checked);
    setBody(composeBodyWithTable(getBodyHtml(), checked, offers));
    setIframeReloadKey(k => k + 1);
  };

  // ── Send ─────────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!selectedAccount) {
      setErrorMessage('Please select a sender account.');
      return;
    }

    const recipients = to.split(',').map((e) => e.trim()).filter(Boolean);
    if (recipients.length === 0) {
      setErrorMessage('Please enter at least one recipient email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = recipients.filter((e) => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      setErrorMessage(`Invalid email address(es): ${invalidEmails.join(', ')}`);
      return;
    }

    if (attachments.length === 0) {
      setErrorMessage('Please attach at least one file before sending.');
      return;
    }

    setDialogState('sending');
    setErrorMessage(null);

    try {
      const ccList = cc.split(',').map((e) => e.trim()).filter(Boolean);
      const allRecipients = [...recipients, ...ccList];

      // The preview iframe already reflects the offers table (or its absence) live,
      // so what's sent is exactly what was shown — no re-merging at send time.
      const finalBodyHtml = getBodyHtml();

      await emailApiService.sendEmail({
        accountId: selectedAccount,
        to: allRecipients,
        subject,
        bodyHtml: finalBodyHtml,
        tenderId,
        attachments,
      });

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;

      const { data: memberData } = await supabase
        .from('base_organisation_members')
        .select('organisation_id')
        .eq('user_id', userId)
        .single();

      const orgId = memberData?.organisation_id;
      const fromEmail = accounts.find((a) => a.id === selectedAccount)?.email || '';

      if (orgId && userId) {
        const { error: commError } = await supabase.from('tender_communications').insert({
          org_id: orgId,
          tender_id: tenderId,
          sent_by_user_id: userId,
          sent_from_email: fromEmail,
          recipients: [
            ...recipients.map((email) => ({ email, type: 'to' })),
            ...ccList.map((email) => ({ email, type: 'cc' })),
          ],
          subject,
          body_html: finalBodyHtml,
          attachments: attachments.map(f => ({ filename: f.name, size: f.size, type: f.type })),
          proposal_file_path: filePath,
          email_provider: 'gmail',
          email_account_id: selectedAccount,
          delivery_status: 'sent',
        });
        if (commError) console.error('Failed to log communication:', commError.message);
      }

      if (markSentToClient) {
        const { error: updateError } = await supabase
          .from('tender')
          .update({ proposal_sent_at: new Date().toISOString(), status: 'sent_to_client' })
          .eq('id', tenderId);
        if (updateError) console.error('Failed to update tender status:', updateError.message);

        if (sfTenderId) salesforceImportService.updateTenderStageInSf(sfTenderId, 'Complete');
      }

      setDialogState('sent');
      onSent();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send email';
      setErrorMessage(msg);
      setDialogState('ready');
    }
  };

  if (!isOpen) return null;

  const isBusy = dialogState === 'loading' || dialogState === 'sending';
  const canSend =
    dialogState === 'ready' &&
    Boolean(selectedAccount) &&
    to.trim().length > 0 &&
    attachments.length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h3 className="text-xl font-semibold flex items-center gap-2 text-gray-900">
            <Mail className="w-5 h-5 text-blue-600" />
            Send Proposal to Client
          </h3>
          <button onClick={onClose} disabled={isBusy} type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

          {/* Success */}
          {dialogState === 'sent' && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Proposal Sent!</h4>
              <p className="text-gray-500">
                Your proposal has been successfully sent to {clientName || 'the client'}.
              </p>
              <button onClick={onClose} type="button"
                className="mt-6 px-8 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                Close
              </button>
            </div>
          )}

          {/* Loading */}
          {dialogState === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-4" />
              <p className="text-gray-500">Loading email accounts and templates…</p>
            </div>
          )}

          {/* Fatal error */}
          {dialogState === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
              <p className="text-red-600 font-medium">{errorMessage}</p>
              <button onClick={loadInitialData} type="button"
                className="mt-4 px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium">
                Try again
              </button>
            </div>
          )}

          {/* Email form */}
          {(dialogState === 'ready' || dialogState === 'sending') && (
            <div className="space-y-4">
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-lg">
                  {errorMessage}
                </div>
              )}

              {accounts.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-yellow-800 text-sm mb-2">No Gmail accounts connected.</p>
                  <a href="/tender/email" target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">
                    Connect an account in Email Management
                  </a>
                </div>
              ) : (
                <>
                  {/* From */}
                  <div className="flex flex-col md:flex-row gap-4 p-4 bg-gray-50 rounded-lg border">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        From <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedAccount}
                        onChange={(e) => handleAccountChange(e.target.value)}
                        disabled={isBusy}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                      >
                        {accounts.map((acc) => (
                          <option key={acc.id} value={acc.id} disabled={!acc.is_active}>
                            {acc.email}{!acc.is_active ? ' (Inactive)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Template selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Template</label>
                    {templates.length === 0 ? (
                      <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg px-3 py-2">
                        No email templates found.{' '}
                        <a href="/tender/email" target="_blank" rel="noreferrer" className="text-blue-600 underline">
                          Create one in Email Management
                        </a>
                      </div>
                    ) : (
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        disabled={isBusy}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                      >
                        <option value="">— Select a template —</option>
                        {templates.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.name}{t.is_default ? ' (Default)' : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={to} onChange={(e) => setTo(e.target.value)}
                      placeholder="client@example.com" disabled={isBusy}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                    />
                    <p className="text-xs text-gray-400 mt-1">Separate multiple addresses with commas</p>
                  </div>

                  {/* CC */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CC</label>
                    <input type="text" value={cc} onChange={(e) => setCc(e.target.value)}
                      placeholder="cc@example.com" disabled={isBusy}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                      disabled={isBusy}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                    />
                  </div>

                  {/* Message body — iframe editable, same as RFI compose */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium text-gray-700">Message</label>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Eye className="h-3 w-3" /> Click any text to edit
                      </span>
                    </div>
                    <iframe
                      key={iframeReloadKey}
                      ref={editableIframeRef}
                      srcDoc={
                        /<!DOCTYPE\s+html|<html\b/i.test(body)
                          ? body
                          : `<!DOCTYPE html><html><body style="font-family:'Segoe UI',Arial,sans-serif;color:#1a1a2e;padding:24px;margin:0;">${body}</body></html>`
                      }
                      title="Email editor"
                      className="w-full border rounded-md bg-white"
                      style={{ height: '420px' }}
                      sandbox="allow-same-origin"
                      onLoad={() => {
                        const doc = editableIframeRef.current?.contentDocument;
                        if (doc) doc.designMode = 'on';
                      }}
                    />
                  </div>

                  {/* Attachments — manual upload only */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Attachments <span className="text-red-500">*</span>
                    </label>

                    <div
                      {...getRootProps()}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-50 transition-all"
                    >
                      <input {...getInputProps()} />
                      <Paperclip className="w-5 h-5 mx-auto mb-1 text-gray-400" />
                      <p className="text-sm text-gray-600">Drag & drop PDF files here, or click to select</p>
                      <p className="text-xs text-gray-400 mt-1">At least one attachment is required to send</p>
                    </div>

                    {attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {attachments.map((file, idx) => (
                          <div
                            key={`${file.name}-${idx}`}
                            className="flex items-center justify-between bg-gray-50 border rounded-lg px-3 py-2 text-sm"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{file.name}</span>
                              <span className="text-gray-400 text-xs flex-shrink-0">({(file.size / 1024).toFixed(1)} KB)</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(idx)}
                              className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Send options */}
                  <div className="space-y-2.5 pt-1 border-t">
                    <label className="flex items-start gap-2.5 text-sm text-gray-700 pt-3">
                      <input
                        type="checkbox"
                        checked={includeOffersTable}
                        onChange={(e) => handleToggleOffersTable(e.target.checked)}
                        disabled={isBusy || offers.length === 0}
                        className="mt-0.5"
                      />
                      <span>
                        Include retailer offers comparison table
                        {offers.length > 0 ? (
                          <span className="text-gray-400">
                            {' '}({offers.length} offer{offers.length === 1 ? '' : 's'} compared, selected one highlighted). Add{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px] font-mono">{'{{Offers Table}}'}</code>{' '}
                            in the template body to control exactly where it appears — otherwise it's placed just above the signature.
                          </span>
                        ) : (
                          <span className="text-gray-400"> (no offers found for this tender)</span>
                        )}
                      </span>
                    </label>

                    <label className="flex items-start gap-2.5 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={markSentToClient}
                        onChange={(e) => setMarkSentToClient(e.target.checked)}
                        disabled={isBusy}
                        className="mt-0.5"
                      />
                      <span>
                        Mark tender as <strong>Sent to Client</strong> after sending
                        <span className="text-gray-400"> (uncheck for reminders/follow-ups that shouldn't change the tender status)</span>
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {(dialogState === 'ready' || dialogState === 'sending') && accounts.length > 0 && (
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex items-center justify-between flex-shrink-0">
            <p className="text-xs text-gray-500">
              {attachments.length === 0 && (
                <span className="text-amber-600">Please attach at least one file to send</span>
              )}
            </p>
            <div className="flex gap-3">
              <button onClick={onClose} disabled={isBusy} type="button"
                className="px-5 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={!canSend || dialogState === 'sending'}
                type="button"
                title={attachments.length === 0 ? 'Please attach at least one file before sending' : undefined}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg font-medium transition-all disabled:opacity-50 shadow-md"
              >
                {dialogState === 'sending' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4" /> Send Proposal</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
